import { FC } from 'react'
import { Alert, Box, Chip, Stack, Typography } from '@mui/material'
import {
  CORES_STATUS_ITEM,
  ROTULOS_CODIGO_MOTIVO,
  ROTULOS_SITUACAO_ARQUIVO,
  ROTULOS_STATUS_ITEM,
} from '@/constants/statusChecklist'
import type { ItemBloqueanteChecklist, StatusItemChecklist, VersaoChecklistReserva } from '@/types/checklist'
import type { ItemChecklistReserva } from '@/types/estruturacao'
import { formatDate, formatDateTime } from '@/utils/formatters'
import { statusDoItem } from '@/utils/checklistReserva'

/**
 * Peças de apresentação do checklist versionado (AN-PP3 §9), comuns ao consultor e ao
 * back-office. Nada aqui calcula: status, validade, motivo e versão vêm do backend.
 */

/** Chip do status do item, com os rótulos e cores de `statusChecklist.ts`. */
export const ChipStatusItem: FC<{ status: StatusItemChecklist }> = ({ status }) => (
  <Chip
    size="small"
    color={CORES_STATUS_ITEM[status]}
    variant={status === 'FALTANDO' || status === 'SUBSTITUIDO' ? 'outlined' : 'filled'}
    label={ROTULOS_STATUS_ITEM[status]}
    data-status={status}
  />
)

/**
 * Linhas de detalhe do item: situação dos arquivos em verificação, motivo da recusa, validade,
 * "Reaproveitado", orientação e versões anteriores. `mostrarAutor` só no back-office — a tela do
 * consultor NUNCA exibe o autor da decisão, mesmo que ele viesse na resposta (Q-PP1-7).
 */
export const DetalhesItemChecklist: FC<{ item: ItemChecklistReserva; mostrarAutor?: boolean }> = ({
  item,
  mostrarAutor = false,
}) => {
  const status = statusDoItem(item)
  const decisao = item.ultimaDecisao ?? null
  const situacoes = Array.from(
    new Set((item.situacaoArquivos ?? []).map((s) => ROTULOS_SITUACAO_ARQUIVO[s] ?? s)),
  )
  const historico = item.historico ?? []
  const linhas: { chave: string; texto: string; cor?: string }[] = []

  if (status === 'QUARENTENA' && situacoes.length > 0) {
    linhas.push({ chave: 'situacao', texto: situacoes.join(' · '), cor: 'warning.main' })
  }
  if (status === 'RECUSADO' && decisao?.decisao === 'RECUSAR') {
    const codigo = decisao.codigoMotivo ? ` (${ROTULOS_CODIGO_MOTIVO[decisao.codigoMotivo] ?? decisao.codigoMotivo})` : ''
    linhas.push({
      chave: 'recusa',
      texto: `Motivo da recusa${codigo}: ${decisao.motivo || 'não informado'}`,
      cor: 'error.main',
    })
  }
  if (item.validoAte && status === 'VENCIDO') {
    linhas.push({ chave: 'validade', texto: `Validade vencida em ${formatDate(item.validoAte)}`, cor: 'error.main' })
  } else if (item.validoAte) {
    linhas.push({ chave: 'validade', texto: `Válido até ${formatDate(item.validoAte)}` })
  }
  if (mostrarAutor && decisao?.autorNome) {
    linhas.push({
      chave: 'autor',
      texto: `${decisao.decisao === 'VALIDAR' ? 'Validado' : 'Recusado'} por ${decisao.autorNome} em ${formatDateTime(decisao.em)}`,
    })
  }
  if (item.orientacao) linhas.push({ chave: 'orientacao', texto: item.orientacao })

  if (linhas.length === 0 && !item.reaproveitado && historico.length === 0) return null

  return (
    <Box sx={{ mt: 0.25 }}>
      {item.reaproveitado && (
        <Chip size="small" variant="outlined" color="info" label="Reaproveitado" sx={{ mr: 0.5, mb: 0.25 }} />
      )}
      {linhas.map((l) => (
        <Typography
          key={l.chave}
          variant="caption"
          component="div"
          color={l.cor ?? 'text.secondary'}
          data-detalhe={l.chave}
        >
          {l.texto}
        </Typography>
      ))}
      {historico.length > 0 && (
        <Typography variant="caption" component="div" color="text.secondary" data-detalhe="historico">
          Versões anteriores:{' '}
          {historico
            .map((h) => `versão ${h.versao} (${ROTULOS_STATUS_ITEM[h.status] ?? h.status} em ${formatDateTime(h.em)})`)
            .join('; ')}
        </Typography>
      )}
    </Box>
  )
}

/**
 * Cabeçalho "Checklist versão N" e, quando a versão presa não é mais a vigente, o aviso de que a
 * reserva segue a versão de quando foi criada (P-10).
 */
export const CabecalhoVersaoChecklist: FC<{ versao?: VersaoChecklistReserva | null }> = ({ versao }) => {
  if (!versao) return null
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2">Checklist versão {versao.numero}</Typography>
        {versao.ehVigente && <Chip size="small" variant="outlined" label="Vigente" />}
      </Stack>
      {!versao.ehVigente && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Esta reserva segue a versão {versao.numero} (vigente quando foi criada).
        </Alert>
      )}
    </Box>
  )
}

/** Recusa 422 por checklist com os itens que barraram (`data` do backend). */
export const AlertaItensBloqueantes: FC<{ mensagem: string; itens: ItemBloqueanteChecklist[] }> = ({
  mensagem,
  itens,
}) => (
  <Alert severity="error" sx={{ mt: 2 }} role="alert">
    {mensagem}
    {itens.length > 0 && (
      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }} aria-label="Itens que impedem">
        {itens.map((i) => (
          <li key={i.chave ?? i.rotulo}>
            <Typography variant="body2" component="span">
              {i.rotulo}
              {i.status ? ` — ${ROTULOS_STATUS_ITEM[i.status] ?? i.status}` : ''}
            </Typography>
          </li>
        ))}
      </Box>
    )}
  </Alert>
)
